import { Modal, ModalBody, ModalHeader } from "flowbite-react";
import React, { useEffect, useRef } from "react";
import Hls from "hls.js";

interface PreviewMediaModalProps {
  isOpen: boolean;
  toggle: () => void;
  videoUrl: string;
}

const PreviewMediaModal: React.FC<PreviewMediaModalProps> = ({
  isOpen,
  toggle,
  videoUrl,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  useEffect(() => {
    if (!isOpen) {
      // Clean up when modal closes
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.src = "";
        videoRef.current.load();
      }
      return;
    }

    if (!videoUrl) return;

    // Wait for video element to be ready
    const initVideo = () => {
      if (!videoRef.current) {
        setTimeout(initVideo, 50);
        return;
      }

      const video = videoRef.current;

      if (Hls.isSupported()) {
        // Cleanup previous HLS instance
        if (hlsRef.current) {
          hlsRef.current.destroy();
        }

        const hls = new Hls();
        hls.loadSource(videoUrl);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          video.play().catch((error) => {
            console.error("Auto-play failed:", error);
          });
        });

        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.error("Fatal network error, trying to recover");
                hls.startLoad();
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                console.error("Fatal media error, trying to recover");
                hls.recoverMediaError();
                break;
              default:
                console.error("Fatal error, cannot recover");
                hls.destroy();
                break;
            }
          }
        });

        hlsRef.current = hls;
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        // Native HLS support (Safari)
        video.src = videoUrl;
        video.addEventListener("loadedmetadata", () => {
          video.play().catch((error) => {
            console.error("Auto-play failed:", error);
          });
        });
      } else {
        // Fallback for other formats
        video.src = videoUrl;
        video.addEventListener("loadedmetadata", () => {
          video.play().catch((error) => {
            console.error("Auto-play failed:", error);
          });
        });
      }
    };

    initVideo();
  }, [videoUrl, isOpen]);

  return (
    <Modal show={isOpen} onClose={toggle} size="lg">
      <ModalHeader className="border-b border-gray-200 p-6! dark:border-gray-700">
        <strong>Preview Media</strong>
      </ModalHeader>
      <ModalBody>
        {videoUrl ? (
          <video ref={videoRef} className="w-full" controls>
            <track kind="captions" />
            Your browser does not support the video tag.
          </video>
        ) : (
          <p className="text-center">No video available</p>
        )}
      </ModalBody>
    </Modal>
  );
};

export default PreviewMediaModal;
